import { useState, useEffect } from 'react';

interface UseOrganizationSelectionProps {
  sessionKey?: string;
}

export const useOrganizationSelection = ({ sessionKey = 'selectedOrganizationIds' }: UseOrganizationSelectionProps = {}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Load selected IDs from session storage on mount
  useEffect(() => {
    const storedIds = sessionStorage.getItem(sessionKey);
    if (storedIds) {
      try {
        const parsedIds = JSON.parse(storedIds);
        if (Array.isArray(parsedIds)) {
          const validIds = parsedIds.filter(id => id != null);
          setSelectedIds(new Set(validIds));
        }
      } catch (error) {

        sessionStorage.removeItem(sessionKey);
      }
    }
  }, [sessionKey]);

  // Save selected IDs to session storage whenever they change
  useEffect(() => {
    const validIds = Array.from(selectedIds).filter(id => id != null);
    sessionStorage.setItem(sessionKey, JSON.stringify(validIds));
  }, [selectedIds, sessionKey]);

  const toggleSelection = (organizationId: string) => {
    if (!organizationId) {

      return;
    }

    setSelectedIds(prevIds => {
      const newIds = new Set(prevIds);
      if (newIds.has(organizationId)) {
        newIds.delete(organizationId);
      } else {
        newIds.add(organizationId);
      }
      return newIds;
    });
  };

  const selectMultiple = (organizationIds: string[]) => {
    setSelectedIds(prevIds => {
      const newIds = new Set(prevIds);
      organizationIds.forEach(id => {
        if (id) newIds.add(id);
      });
      return newIds;
    });
  };

  const unselectMultiple = (organizationIds: string[]) => {
    setSelectedIds(prevIds => {
      const newIds = new Set(prevIds);
      organizationIds.forEach(id => {
        if (id) newIds.delete(id);
      });
      return newIds;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const isSelected = (organizationId: string) => selectedIds.has(organizationId);

  return {
    selectedIds,
    toggleSelection,
    selectMultiple,
    unselectMultiple,
    clearSelection,
    isSelected,
    selectedCount: selectedIds.size
  };
}; 